/**
 * Team member self-service (Phase SC-22d).
 *
 * Wraps `/api/me/team-*` in eduviaos-backend (`app/domains/team_pay/me_routes.py`).
 * Shapes mirror `app/domains/team_pay/schemas.py`. Amounts are integer
 * PAISE; dates are IST calendar days (`YYYY-MM-DD`).
 *
 * `GET /api/me/team-profile` is always answerable (it says whether the
 * caller is a linked team member and which self-service toggles are on);
 * the rest sit behind the `payroll` module gate and 404 for non-members.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "../api";

export type PayBasis = "monthly" | "daily" | "hourly";
export type ApprovalMode =
  | "admin_approves_each"
  | "self_auto_approve"
  | "geo_auto_approve"
  | "admin_marks_only";
export type AttendanceStatus = "present" | "half_day" | "absent" | "leave" | "holiday" | "week_off";
export type ApprovalStatus = "pending" | "approved" | "rejected";
export type LeaveType = "casual" | "sick" | "earned" | "unpaid" | "other";
export type LeaveStatus = "pending" | "approved" | "rejected" | "cancelled";

export interface TeamAttendanceRecord {
  id: string;
  day: string;
  status: AttendanceStatus;
  approval_status: ApprovalStatus;
  check_in_at: string | null;
  check_out_at: string | null;
  hours_worked: number | null;
  distance_m: number | null;
  inside_geofence: boolean | null;
  note: string | null;
}

export interface TeamProfile {
  is_team_member: boolean;
  module_enabled: boolean;
  staff_payee_id: string | null;
  name: string | null;
  role_label: string | null;
  pay_basis: PayBasis | null;
  attendance_mode: ApprovalMode | null;
  can_self_check_in: boolean;
  show_pay: boolean;
  allow_leave_requests: boolean;
  geofence_configured: boolean;
  geofence_radius_m: number | null;
  max_accuracy_m: number | null;
  location_label: string | null;
  today: string;
  today_record: TeamAttendanceRecord | null;
  weekly_off_days: number[];
}

export interface CheckInBody {
  lat?: number | null;
  lng?: number | null;
  accuracy_m?: number | null;
  note?: string | null;
}

export interface CheckInResponse {
  record: TeamAttendanceRecord;
  message: string;
  inside_geofence: boolean | null;
  distance_m: number | null;
}

export interface RegisterDay {
  day: string;
  status: AttendanceStatus | null;
  approval_status: ApprovalStatus | null;
  record_id: string | null;
  week_off: boolean;
  holiday: boolean;
}

export interface MyAttendanceMonth {
  year: number;
  month: number;
  days: RegisterDay[];
  present: number;
  absent: number;
  leave: number;
  pending: number;
}

export interface Instalment {
  year: number;
  month: number;
  amount_paise: number;
}

export interface MyAdvance {
  id: string;
  amount_paise: number;
  given_on: string;
  plan: string;
  emi_months: number | null;
  schedule: Instalment[];
  recovered_paise: number;
  outstanding_paise: number;
}

export interface MyAdjustment {
  id: string;
  kind: "incentive" | "bonus" | "deduction" | "reimbursement";
  amount_paise: number;
  effective_on: string;
  note: string | null;
}

export interface MyPayment {
  id: string;
  amount_paise: number;
  paid_on: string;
  method: "cash" | "upi" | "bank" | "other";
  allocation_summary: string;
}

export interface MyPayslip {
  pay_run_id: string;
  year: number;
  month: number;
  gross_paise: number;
  deduction_paise: number;
  net_paise: number;
  note: string | null;
}

export interface MyPay {
  year: number;
  month: number;
  as_of: string;
  pay_basis: PayBasis;
  earned_paise: number;
  explanation: string;
  additions_paise: number;
  deductions_paise: number;
  recovery_paise: number;
  paid_paise: number;
  owed_today_paise: number;
  advance_outstanding_paise: number;
  advances: MyAdvance[];
  adjustments: MyAdjustment[];
  payments: MyPayment[];
  payslips: MyPayslip[];
}

export interface MyLeave {
  id: string;
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  half_day: boolean;
  reason: string | null;
  status: LeaveStatus;
  days_requested: number;
  paid_days: number;
  unpaid_days: number;
  decision_note: string | null;
  created_at: string;
}

export interface MyLeaveBalance {
  leave_year_start: string;
  leave_year_end: string;
  accrued_days: number;
  used_paid_days: number;
  pending_days: number;
  available_days: number;
}

export interface CreateLeaveBody {
  leave_type: LeaveType;
  start_date: string;
  end_date: string;
  half_day: boolean;
  reason: string | null;
}

export interface MyHoliday {
  name: string;
  start_date: string;
  end_date: string;
  kind: string;
}

export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  casual: "Casual",
  sick: "Sick",
  earned: "Earned",
  unpaid: "Unpaid",
  other: "Other",
};

export const ADJUSTMENT_LABELS: Record<MyAdjustment["kind"], string> = {
  incentive: "Incentive",
  bonus: "Bonus",
  reimbursement: "Reimbursement",
  deduction: "Deduction",
};

const ROOT = ["team-me"] as const;
export const TEAM_KEYS = {
  profile: [...ROOT, "profile"] as const,
  attendance: [...ROOT, "attendance"] as const,
  pay: [...ROOT, "pay"] as const,
  leaves: [...ROOT, "leaves"] as const,
  balance: [...ROOT, "leave-balance"] as const,
  holidays: [...ROOT, "holidays"] as const,
};

export function useTeamProfile(enabled = true) {
  return useQuery<TeamProfile>({
    queryKey: TEAM_KEYS.profile,
    queryFn: () => api.get<TeamProfile>("/api/me/team-profile"),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}

export function useMyTeamAttendance(month: string, enabled = true) {
  return useQuery<MyAttendanceMonth>({
    queryKey: [...TEAM_KEYS.attendance, month],
    queryFn: () => api.get<MyAttendanceMonth>(`/api/me/team-attendance?month=${month}`),
    enabled,
    retry: false,
  });
}

export function useMyTeamPay(month: string, enabled = true) {
  return useQuery<MyPay>({
    queryKey: [...TEAM_KEYS.pay, month],
    queryFn: () => api.get<MyPay>(`/api/me/team-pay?month=${month}`),
    enabled,
    retry: false,
  });
}

export function useMyLeaves(enabled = true) {
  return useQuery<{ items: MyLeave[]; total: number }>({
    queryKey: TEAM_KEYS.leaves,
    queryFn: () => api.get<{ items: MyLeave[]; total: number }>("/api/me/team-leaves"),
    enabled,
    retry: false,
  });
}

export function useMyLeaveBalance(enabled = true) {
  return useQuery<MyLeaveBalance>({
    queryKey: TEAM_KEYS.balance,
    queryFn: () => api.get<MyLeaveBalance>("/api/me/team-leaves/balance"),
    enabled,
    retry: false,
  });
}

export function useMyHolidays(from: string, to: string, enabled = true) {
  return useQuery<{ items: MyHoliday[] }>({
    queryKey: [...TEAM_KEYS.holidays, from, to],
    queryFn: () => api.get<{ items: MyHoliday[] }>(`/api/me/team-holidays?from=${from}&to=${to}`),
    enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ROOT });
}

export function useCheckIn(kind: "check-in" | "check-out") {
  const invalidate = useInvalidate();
  return useMutation<CheckInResponse, ApiError, CheckInBody>({
    mutationFn: (body) => api.post<CheckInResponse>(`/api/me/team-attendance/${kind}`, body),
    onSuccess: invalidate,
  });
}

export function useRequestLeave() {
  const invalidate = useInvalidate();
  return useMutation<MyLeave, ApiError, CreateLeaveBody>({
    mutationFn: (body) => api.post<MyLeave>("/api/me/team-leaves", body),
    onSuccess: invalidate,
  });
}

export function useCancelLeave() {
  const invalidate = useInvalidate();
  return useMutation<MyLeave, ApiError, string>({
    mutationFn: (id) => api.post<MyLeave>(`/api/me/team-leaves/${id}/cancel`, {}),
    onSuccess: invalidate,
  });
}

// ── Helpers ────────────────────────────────────────────────────────────

const INR = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

export function formatRupees(paise: number): string {
  return INR.format(paise / 100);
}

export function istToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

export function parseDay(iso: string): [number, number, number] {
  const [y = 1970, m = 1, d = 1] = iso.split("-").map(Number);
  return [y, m, d];
}

/** `[year, month]` → `YYYY-MM`. */
export function monthKey(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, "0")}`;
}

export function shiftMonth(year: number, month: number, delta: number): [number, number] {
  const d = new Date(year, month - 1 + delta, 1);
  return [d.getFullYear(), d.getMonth() + 1];
}

export function monthTitle(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
}

export function shortDay(iso: string): string {
  const [y, m, d] = parseDay(iso);
  return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
}

export function clockTime(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
  });
}
